import * as React from "react";
import { useState, useEffect } from "react";
import type { Selection } from "@heroui/react";
import {
  addToast,
  Button,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Pagination,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useApi } from "../hooks/useApi";
import { useTranslation } from "react-i18next";

export const VocabularyPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState<string>("");
  const [skippingWord, setSkippingWord] = useState<string>("");
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]));
  const [skippingWords, setSkippingWords] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const { loading, post, get, del } = useApi();
  const { t } = useTranslation();

  const fetchSkippingWords = async (): Promise<void> => {
    const data = await get("/spell/skipping-words");
    setSkippingWords(data);
  };

  const addSkippingWord = async (word: string): Promise<void> => {
    await post("/spell/skipping-words", { word });
    await fetchSkippingWords();
    setIsModalOpen(false);
  };

  const filteredItems = React.useMemo(() => {
    if (!filter) return skippingWords;
    if (filter === "*") return skippingWords;

    return skippingWords.filter((word) => word.toLowerCase().includes(filter.toLowerCase()));
  }, [filter, skippingWords]);

  const pages = React.useMemo(() => {
    if (filteredItems.length === 0) return 1;
    return Math.ceil(filteredItems.length / rowsPerPage);
  }, [filteredItems]);

  const selectedWordCount = React.useMemo(() => {
    if (selectedKeys === "all") {
      return filteredItems.length;
    }
    return selectedKeys.size;
  }, [selectedKeys, filteredItems]);

  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;

    return filteredItems.slice(start, end);
  }, [filteredItems, page]);

  useEffect(() => {
    fetchSkippingWords();
  }, []);

  const handleAddSkippingWord = async () => {
    const trimmedWord = skippingWord.trim();
    if (!trimmedWord) {
      addToast({
        title: t("enterWordToSearch"),
        severity: "warning",
        color: "warning",
      });
      return;
    }

    if (skippingWords.includes(trimmedWord)) {
      addToast({
        title: t("wordAlreadyExists"),
        severity: "primary",
        color: "primary",
      });
      return;
    }
    await addSkippingWord(trimmedWord);
    setSkippingWord("");
  };

  const handleDeleteSelectedSkippingWords = async () => {
    if (typeof selectedKeys !== "string" && selectedKeys.size === 0) {
      addToast({ title: "Үг сонгоно уу.", severity: "warning", color: "warning" });
      return;
    }

    const wordsToDelete: string[] = [];
    if (selectedKeys === "all") {
      wordsToDelete.push(...skippingWords);
    } else {
      (Array.from(selectedKeys) as string[]).forEach((key) => {
        wordsToDelete.push(key);
      });
    }
    const payload = { words: wordsToDelete };
    await del("/spell/skipping-words", payload);
    await fetchSkippingWords();
    addToast({ title: t("deletedSuccessfully"), severity: "success", color: "success" });
    setSelectedKeys(new Set([]));
  };

  const topContent = () => {
    return (
      <div>
        <div className="flex items-center justify-between w-full">
          <Input
            startContent={<Icon icon="iconoir:filter" />}
            classNames={{
              base: "w-[50%]",
              inputWrapper: "bg-white",
            }}
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("filter")}
            isClearable
            onClear={() => setFilter("")}
          />
          <Button
            variant="shadow"
            endContent={<Icon icon="mdi:plus" />}
            onPress={() => setIsModalOpen(true)}
            color="primary"
            type="submit"
          >
            {t("add")}
          </Button>
        </div>
        <div className="flex items-center justify-center h-6 mt-2 text-sm text-zinc-400">
          <Button
            className={`${selectedWordCount > 0 ? "visible ml-2 font-semibold" : "invisible ml-2"}`}
            variant="light"
            size="sm"
            onPress={handleDeleteSelectedSkippingWords}
            color="danger"
            endContent={<Icon width={20} height={20} icon="mdi:delete" />}
          >
            {selectedWordCount > 0
              ? selectedWordCount === skippingWords.length
                ? t("deleteAllWords")
                : t("deleteWords", { count: selectedWordCount })
              : ""}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Table
        onSelectionChange={setSelectedKeys}
        isCompact
        removeWrapper
        topContentPlacement="outside"
        aria-label="Vocabulary table"
        className="table-fixed w-full"
        topContent={topContent()}
        selectedKeys={selectedKeys}
        selectionMode="multiple"
        classNames={{
          th: "bg-white",
        }}
        bottomContent={
          pages > 1 ? (
            <div className="flex w-full justify-center">
              <Pagination
                isCompact
                showControls
                showShadow
                color="primary"
                page={page}
                total={pages}
                onChange={(newPage) => setPage(newPage)}
                classNames={{
                  item: "bg-white",
                  next: "bg-white",
                  prev: "bg-white",
                }}
              />
            </div>
          ) : null
        }
      >
        <TableHeader>
          <TableColumn className="w-[10%]">№</TableColumn>
          <TableColumn className="w-[80%]">{t("vocabularyList")}</TableColumn>
        </TableHeader>
        <TableBody
          isLoading={loading}
          loadingContent={
            <div className="text-center">
              <Spinner color="default" variant="wave" />
            </div>
          }
          emptyContent={!loading ? t("listIsEmpty") : undefined}
        >
          {!loading &&
            items.length > 0 &&
            items.map((word, index) => (
              <TableRow key={word}>
                <TableCell className="w-[10%]">{index + 1}</TableCell>
                <TableCell className="w-[80%] break-words">{word}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="sm"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">{t("addNewWord")}</ModalHeader>
              <ModalBody className="flex flex-col gap-4">
                <Input
                  type="text"
                  value={skippingWord}
                  onChange={(e) => setSkippingWord(e.target.value)}
                  placeholder={t("inputVocabularyWord")}
                />
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  {t("close")}
                </Button>
                <Button
                  isLoading={loading}
                  onPress={handleAddSkippingWord}
                  color="primary"
                  type="submit"
                >
                  {t("save")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
